const PROTOCOL = 'CYLM1';
const MAX_LINE_CHARS = 384;
const MAX_MESSAGE_CHARS = 160;
const MAX_CYLMCHAT_REPLY_CHARS = 38;
const MAX_NATIVE_REPLY_CHARS = 80;
const DEFAULT_PROXY_TIMEOUT_MS = 9000;

export const NATIVE_CYLM_SURFACE = 'native-cylm-app-v1';
export const NATIVE_CYLM_TRANSPORT = 'native-console-v1';
export const NATIVE_CYLM_PEER = { id: 'cylm.native', name: 'CYLM' } as const;

export type NativeCylmParseResult =
  | { readonly kind: 'ignore' }
  | { readonly kind: 'hello'; readonly app: string }
  | { readonly kind: 'user'; readonly seq: number; readonly message: string }
  | { readonly kind: 'error'; readonly reason: string; readonly seq?: number };

export interface NativeCylmProxyTurn {
  readonly role: 'user' | 'cylm' | 'system';
  readonly text: string;
}

export interface NativeCylmProxyBody {
  readonly sessionId: string;
  readonly message: string;
  readonly turns: readonly NativeCylmProxyTurn[];
  readonly surface: typeof NATIVE_CYLM_SURFACE;
  readonly transport: typeof NATIVE_CYLM_TRANSPORT;
  readonly retention: 'none';
  readonly peer: typeof NATIVE_CYLM_PEER;
}

export interface NativeCylmBridgeDebugState {
  readonly enabled: true;
  readonly protocol: typeof PROTOCOL;
  readonly sessionId: string;
  readonly surface: typeof NATIVE_CYLM_SURFACE;
  readonly transport: typeof NATIVE_CYLM_TRANSPORT;
  readonly pendingSeq: number | null;
  readonly receivedHellos: number;
  readonly receivedUserTurns: number;
  readonly lastHelloApp: string;
  readonly lastUserSeq: number | null;
  readonly lastUserLength: number | null;
  readonly sentReplies: number;
  readonly sentErrors: number;
  readonly ignoredLines: number;
  readonly malformedLines: number;
  readonly lastError: string;
}

export interface NativeCylmBridge {
  receiveConsoleByte(byte: number): void;
  receiveConsoleText(text: string): void;
  settled(): Promise<void>;
  debugState(): NativeCylmBridgeDebugState;
}

export interface NativeCylmBridgeOptions {
  readonly sessionId?: string;
  readonly sendConsoleText: (text: string) => void;
  readonly requestReply?: (body: NativeCylmProxyBody) => Promise<string>;
  readonly logWarning?: (message: string) => void;
}

export interface NativeCylmProxyRequestOptions {
  readonly endpoint?: string;
  readonly fetcher?: typeof fetch;
  readonly timeoutMs?: number;
}

declare global {
  interface Window {
    __cylmNativeBridgeDebug?: () => NativeCylmBridgeDebugState | undefined;
    __cylmNativeBridgeReceiveConsoleText?: (text: string) => Promise<void>;
  }
}

export function shouldInitNativeCylmBridge(search: string): boolean {
  const params = new URLSearchParams(search);
  if (params.get('cylm') === 'host') return false;
  return params.get('nativeCylmBridge') !== '0';
}

export function serializeNativeCylmUserMessage(seq: number, message: string): string {
  return serializeNativeCylmLine('USER', seq, message);
}

export function serializeNativeCylmReply(seq: number, reply: string): string {
  return serializeNativeCylmLine('REPLY', seq, reply || 'OK.');
}

export function serializeNativeCylmError(seq: number, reason: string): string {
  return serializeNativeCylmLine('ERR', seq, reason || 'HOST OFFLINE.');
}

export function serializeNativeCylmReady(sessionId: string): string {
  const safeSession = sessionId.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 64) || 'session';
  return `${PROTOCOL} READY ${safeSession}\r\n`;
}

export function parseNativeCylmLine(line: string): NativeCylmParseResult {
  const trimmed = line.replace(/[\r\n]+$/g, '').trim();
  if (!trimmed.startsWith(`${PROTOCOL} `)) return { kind: 'ignore' };

  const afterProtocol = trimmed.slice(PROTOCOL.length).trimStart();
  const commandRead = readToken(afterProtocol);
  if (!commandRead) return { kind: 'error', reason: 'missing_command' };

  const command = commandRead.token;
  const rest = commandRead.rest.trimStart();

  if (command === 'HELLO') {
    return { kind: 'hello', app: normalizeNativeCylmText(rest || 'CYLM') };
  }

  if (command !== 'USER') {
    return { kind: 'error', reason: 'unknown_command' };
  }

  const seqRead = readToken(rest);
  if (!seqRead) return { kind: 'error', reason: 'missing_seq' };
  const seq = parseBoundedInt(seqRead.token, 0, 9999);
  if (seq === null) return { kind: 'error', reason: 'invalid_seq' };

  const lenRead = readToken(seqRead.rest.trimStart());
  if (!lenRead) return { kind: 'error', reason: 'missing_length', seq };
  const declaredLength = parseBoundedInt(lenRead.token, 1, MAX_MESSAGE_CHARS);
  if (declaredLength === null) return { kind: 'error', reason: 'invalid_length', seq };

  const message = normalizeNativeCylmTextUnbounded(lenRead.rest.trimStart());
  if (!message) return { kind: 'error', reason: 'empty_message', seq };
  if (message.length !== declaredLength) {
    return { kind: 'error', reason: 'length_mismatch', seq };
  }
  if (message.length > MAX_MESSAGE_CHARS) {
    return { kind: 'error', reason: 'message_too_long', seq };
  }

  return { kind: 'user', seq, message };
}

export function createNativeCylmProxyBody(
  sessionId: string,
  message: string,
): NativeCylmProxyBody {
  return {
    sessionId,
    message: normalizeNativeCylmText(message),
    turns: [],
    surface: NATIVE_CYLM_SURFACE,
    transport: NATIVE_CYLM_TRANSPORT,
    retention: 'none',
    peer: NATIVE_CYLM_PEER,
  };
}

export async function requestNativeCylmProxyReply(
  body: NativeCylmProxyBody,
  options: NativeCylmProxyRequestOptions = {},
): Promise<string> {
  const fetcher = options.fetcher ?? fetch;
  const endpoint = options.endpoint ?? '/api/cylm';
  const timeoutMs = options.timeoutMs ?? DEFAULT_PROXY_TIMEOUT_MS;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetcher(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON.stringify(body),
    });
    const data = await response.json() as { readonly reply?: unknown; readonly error?: unknown };
    if (typeof data.reply === 'string' && data.reply.trim()) {
      return normalizeNativeCylmText(data.reply);
    }
    if (!response.ok) {
      throw new Error(typeof data.error === 'string' ? data.error : `proxy_${response.status}`);
    }
    throw new Error('proxy_empty_reply');
  } finally {
    clearTimeout(timer);
  }
}

export function createNativeCylmBridge(options: NativeCylmBridgeOptions): NativeCylmBridge {
  const sessionId = options.sessionId ?? createSessionId();
  const requestReply = options.requestReply ?? requestNativeCylmProxyReply;
  const tasks = new Set<Promise<void>>();
  let currentLine = '';
  let pendingSeq: number | null = null;
  let receivedHellos = 0;
  let receivedUserTurns = 0;
  let lastHelloApp = '';
  let lastUserSeq: number | null = null;
  let lastUserLength: number | null = null;
  let sentReplies = 0;
  let sentErrors = 0;
  let ignoredLines = 0;
  let malformedLines = 0;
  let lastError = '';

  const sendError = (seq: number, reason: string): void => {
    options.sendConsoleText(serializeNativeCylmError(seq, reason));
    sentErrors += 1;
    lastError = reason;
  };

  const processLine = async (line: string): Promise<void> => {
    const parsed = parseNativeCylmLine(line);
    if (parsed.kind === 'ignore') {
      ignoredLines += 1;
      return;
    }
    if (parsed.kind === 'hello') {
      receivedHellos += 1;
      lastHelloApp = parsed.app;
      return;
    }
    if (parsed.kind === 'error') {
      malformedLines += 1;
      lastError = parsed.reason;
      if (parsed.seq !== undefined) sendError(parsed.seq, parsed.reason);
      return;
    }
    if (pendingSeq !== null) {
      sendError(parsed.seq, 'WAIT');
      return;
    }

    pendingSeq = parsed.seq;
    receivedUserTurns += 1;
    lastUserSeq = parsed.seq;
    lastUserLength = parsed.message.length;
    try {
      const body = createNativeCylmProxyBody(sessionId, parsed.message);
      const reply = await requestReply(body);
      options.sendConsoleText(serializeNativeCylmReply(
        parsed.seq,
        lcdSafeNativeReply(reply, lastHelloApp),
      ));
      sentReplies += 1;
      lastError = '';
    } catch (err) {
      sendError(parsed.seq, 'HOST OFFLINE.');
      options.logWarning?.(err instanceof Error ? err.message : String(err));
    } finally {
      pendingSeq = null;
    }
  };

  const enqueueLine = (line: string): void => {
    const task = processLine(line).catch((err: unknown) => {
      malformedLines += 1;
      lastError = err instanceof Error ? err.message : String(err);
      options.logWarning?.(lastError);
    });
    tasks.add(task);
    void task.finally(() => tasks.delete(task));
  };

  const flushLine = (): void => {
    const line = currentLine.trim();
    currentLine = '';
    if (line) enqueueLine(line);
  };

  const receiveConsoleByte = (byte: number): void => {
    if (!Number.isInteger(byte)) return;
    const b = byte & 0xff;
    if (b === 0x0d || b === 0x0a) {
      flushLine();
      return;
    }
    if (b < 0x20 || b > 0x7e) return;
    currentLine += String.fromCharCode(b);
    if (currentLine.length > MAX_LINE_CHARS) {
      currentLine = '';
      malformedLines += 1;
      lastError = 'line_too_long';
    }
  };

  return {
    receiveConsoleByte,
    receiveConsoleText(text) {
      for (let i = 0; i < text.length; i++) {
        receiveConsoleByte(text.charCodeAt(i));
      }
    },
    async settled() {
      const pending = [...tasks];
      await Promise.allSettled(pending);
    },
    debugState() {
      return {
        enabled: true,
        protocol: PROTOCOL,
        sessionId,
        surface: NATIVE_CYLM_SURFACE,
        transport: NATIVE_CYLM_TRANSPORT,
        pendingSeq,
        receivedHellos,
        receivedUserTurns,
        lastHelloApp,
        lastUserSeq,
        lastUserLength,
        sentReplies,
        sentErrors,
        ignoredLines,
        malformedLines,
        lastError,
      };
    },
  };
}

export function normalizeNativeCylmText(text: string): string {
  return normalizeNativeCylmTextUnbounded(text).slice(0, MAX_MESSAGE_CHARS);
}

export function lcdSafeNativeReply(text: string, app = ''): string {
  const max = app === 'CYLMCHAT' ? MAX_CYLMCHAT_REPLY_CHARS : MAX_NATIVE_REPLY_CHARS;
  const clean = normalizeNativeCylmTextUnbounded(text);
  if (!clean) return 'OK.';
  if (clean.length <= max) return clean;
  const clipped = clean.slice(0, max);
  const lastSpace = clipped.lastIndexOf(' ');
  return (lastSpace >= Math.floor(max * 0.55) ? clipped.slice(0, lastSpace) : clipped).trim() || 'OK.';
}

function serializeNativeCylmLine(command: 'USER' | 'REPLY' | 'ERR', seq: number, text: string): string {
  const safeSeq = parseBoundedInt(String(seq), 0, 9999);
  if (safeSeq === null) throw new Error('invalid Native CyLM sequence');
  const safeText = normalizeNativeCylmText(text) || 'OK.';
  return `${PROTOCOL} ${command} ${safeSeq} ${safeText.length} ${safeText}\r\n`;
}

function readToken(value: string): { readonly token: string; readonly rest: string } | null {
  const trimmed = value.trimStart();
  if (!trimmed) return null;
  const idx = trimmed.search(/\s/);
  if (idx < 0) return { token: trimmed, rest: '' };
  return { token: trimmed.slice(0, idx), rest: trimmed.slice(idx + 1) };
}

function parseBoundedInt(value: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < min || parsed > max) return null;
  return parsed;
}

function normalizeNativeCylmTextUnbounded(text: string): string {
  return text
    .toUpperCase()
    .replace(/[^ A-Z0-9.,:;?!'<>/\\=\-_*]/g, '?')
    .replace(/\s+/g, ' ')
    .trim();
}

function createSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 32);
  }
  return Math.random().toString(36).slice(2).padEnd(12, '0').slice(0, 12);
}
